// The eight case positions explained for learners: the role each one marks, its Hindi marker, the question it answers,
// a model sentence built from engine forms (FormRefs, never typed by hand), the words that call for a case, and the
// rule number of each case read from the engine's noun.rules (one rule group per case, in table order).

import type { FormRef } from '@/prakrit/lib/grammar/formref'
import type {
    Case,
    Gender,
    Lang,
    Num
} from '@/prakrit/lib/grammar/types'

import {
    AV,
    N,
    SUBJ,
    V,
    formOf
} from '@/prakrit/lib/grammar/formref'
import { CASES, NUMBERS } from '@/prakrit/lib/grammar/types'

export const DEFAULT_STEM = 'पुरिस'

/** The three model words of the cases page: a masculine अ-stem, a feminine आ-stem and a neuter अ-stem. */
export const MODEL_STEMS = ['पुरिस', 'माला', 'फल']

export const isCase = (value: string | null | undefined): value is Case => Boolean(value) && (CASES as readonly string[]).includes(value as string)

export const isNum = (value: string | null | undefined): value is Num => Boolean(value) && (NUMBERS as readonly string[]).includes(value as string)

/** nominative → Nominative */
export const titleCase = (value: string): string => (value ? value[0].toUpperCase() + value.slice(1) : value)

/** The short कारक name: करण (के द्वारा, साथ) → करण. */
export const karakShort = (value: string): string => value.split(' (')[0]

export interface CaseInfo {
    /** 1 to 7, or — for सम्बोधन (a use of the first case that tables show as its own row). */
    numeral: string
    role: Lang
    /** The Hindi postposition(s) that carry the same role. */
    marker: string
    asks: Lang
    desc: Lang
}

const sah = formOf(AV('सह'))
const vina = formOf(AV('विणा'))
const bihai = formOf(V('बीह', 'vartaman', 'anya', 'eka'))

export const CASE_INFO: Record<Case, CaseInfo> = {
    prathama: {
        numeral: '1',
        role: { hi: 'कर्ता: जो काम करता है', en: 'Subject: the one who does the action' },
        marker: 'ने / (कोई चिह्न नहीं)',
        asks: { hi: 'कौन? क्या?', en: 'Who? What?' },
        desc: {
            hi: 'वाक्य में काम करने वाला (कर्ता) प्रथमा में आता है।',
            en: 'The doer of the sentence stands in प्रथमा.'
        }
    },
    dvitiya: {
        numeral: '2',
        role: { hi: 'कर्म: जिस पर काम का असर पड़ता है', en: 'Object: what the action is done to' },
        marker: 'को / (कोई चिह्न नहीं)',
        asks: { hi: 'किसको? क्या?', en: 'Whom? What?' },
        desc: {
            hi: 'जिस पर काम का असर पड़ता है (कर्म), वह द्वितीया में आता है।',
            en: 'The object, what the action is done to, stands in द्वितीया.'
        }
    },
    tritiya: {
        numeral: '3',
        role: { hi: 'करण: जिसके द्वारा काम होता है', en: 'Means: by or with which it is done' },
        marker: 'से, के द्वारा, के साथ',
        asks: { hi: 'किससे? किसके साथ?', en: 'By what? With whom?' },
        desc: {
            hi: `जिस साधन से काम होता है, वह तृतीया में आता है। ${sah} (साथ) और ${vina} (बिना) के साथ भी तृतीया आती है।`,
            en: `The means by which an action is done stands in तृतीया. ${sah} (with) and ${vina} (without) also take तृतीया.`
        }
    },
    chaturthi: {
        numeral: '4',
        role: { hi: 'सम्प्रदान: जिसके लिए काम होता है', en: 'Recipient: for whom, to whom' },
        marker: 'के लिए, को',
        asks: { hi: 'किसके लिए? किसको?', en: 'For whom? To whom?' },
        desc: {
            hi: 'जिसके लिए काम होता है, वह चतुर्थी में आता है। प्राकृत में इसके रूप षष्ठी जैसे ही होते हैं (नि.४६)।',
            en: 'The one something is done for stands in चतुर्थी. In Prakrit its forms are the same as the षष्ठी forms (नि.४६).'
        }
    },
    panchami: {
        numeral: '5',
        role: { hi: 'अपादान: जिससे कुछ अलग होता है', en: 'Source: what something moves away from' },
        marker: 'से (अलग होना)',
        asks: { hi: 'किससे? कहाँ से?', en: 'From what? From where?' },
        desc: {
            hi: `जिससे कुछ अलग होता है, वह पंचमी में आता है। जिससे डर लगे, वह भी पंचमी में (${bihai})।`,
            en: `What something moves away from stands in पंचमी. So does what one fears (${bihai}).`
        }
    },
    shashthi: {
        numeral: '6',
        role: { hi: 'सम्बन्ध: किसका है', en: 'Relation: whose, of what' },
        marker: 'का, के, की',
        asks: { hi: 'किसका? किसकी? किसके?', en: 'Whose? Of what?' },
        desc: {
            hi: 'जिसका कुछ है, वह षष्ठी में आता है। इसके रूप चतुर्थी जैसे ही होते हैं; अर्थ वाक्य से समझ आता है (नि.४६)।',
            en: 'The one something belongs to stands in षष्ठी. Its forms are the same as the चतुर्थी forms; the sentence shows which is meant (नि.४६).'
        }
    },
    saptami: {
        numeral: '7',
        role: { hi: 'अधिकरण: जहाँ या जब काम होता है', en: 'Place or time: where or when it happens' },
        marker: 'में, पर',
        asks: { hi: 'किसमें? किस पर? कब?', en: 'In what? On what? When?' },
        desc: {
            hi: 'जहाँ या जब काम होता है, वह सप्तमी में आता है।',
            en: 'The place or the time of the action stands in सप्तमी.'
        }
    },
    sambodhan: {
        numeral: '—',
        role: { hi: 'सम्बोधन: किसी को पुकारना', en: 'Address: calling someone' },
        marker: 'हे, अरे',
        asks: { hi: 'किसे पुकारा?', en: 'Who is being called?' },
        desc: {
            hi: 'जिसे पुकारा जाता है, वह सम्बोधन में आता है। यह प्रथमा का ही एक प्रयोग है, पर तालिकाएँ इसे अलग पंक्ति में दिखाती हैं।',
            en: 'The one being called stands in the vocative. It is a use of the first case, but tables show it as its own row.'
        }
    }
}

// ---------------------------------------------------------------- model sentences of the cases page

export interface CaseExample {
    refs: FormRef[]
    /** Index of the word in this case. */
    focus: number
    hi: string
    en: string
    /** A call (सम्बोधन): a comma after the first word and an exclamation mark at the end. */
    call?: boolean
}

export const CASE_EXAMPLES: Record<Case, CaseExample> = {
    prathama: {
        refs: [N('बालअ', 'prathama', 'eka'), V('पढ', 'vartaman', 'anya', 'eka')],
        focus: 0,
        hi: 'बालक पढ़ता है।',
        en: 'The boy reads.'
    },
    dvitiya: {
        refs: [N('बालअ', 'prathama', 'eka'), N('पोत्थअ', 'dvitiya', 'eka'), V('पढ', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'बालक पुस्तक पढ़ता है।',
        en: 'The boy reads a book.'
    },
    tritiya: {
        refs: [N('पुरिस', 'prathama', 'eka'), N('कर', 'tritiya', 'eka'), N('फल', 'dvitiya', 'eka'), V('गिण्ह', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'आदमी हाथ से फल लेता है।',
        en: 'The man takes the fruit with his hand.'
    },
    chaturthi: {
        refs: [SUBJ('anya', 'eka', 'stri'), N('माआ', 'chaturthi', 'eka'), N('पुप्फ', 'dvitiya', 'bahu'), V('आणे', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'वह माता के लिए फूल लाती है।',
        en: 'She brings flowers for her mother.'
    },
    panchami: {
        refs: [N('बालअ', 'prathama', 'eka'), N('गाम', 'panchami', 'eka'), V('आगच्छ', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'बालक गाँव से आता है।',
        en: 'The boy comes from the village.'
    },
    shashthi: {
        refs: [N('बालअ', 'prathama', 'eka'), N('गुरु', 'shashthi', 'eka'), N('पोत्थअ', 'dvitiya', 'eka'), V('पढ', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'बालक गुरु की पुस्तक पढ़ता है।',
        en: 'The boy reads the teacher’s book.'
    },
    saptami: {
        refs: [N('बालअ', 'prathama', 'bahu'), N('उववण', 'saptami', 'eka'), V('खेल', 'vartaman', 'anya', 'bahu')],
        focus: 1,
        hi: 'बालक उपवन में खेलते हैं।',
        en: 'The boys play in the garden.'
    },
    sambodhan: {
        refs: [N('पुत्त', 'sambodhan', 'eka'), AV('अत्थ'), V('आगच्छ', 'vidhi', 'madhyam', 'eka')],
        focus: 0,
        hi: 'हे पुत्र, यहाँ आओ!',
        en: 'O son, come here!',
        call: true
    }
}

/** The words of a sentence (engine forms) and the sentence as one line. */
export const sentenceOf = (refs: FormRef[], call = false): { words: string[], text: string } => {
    const words = refs.map(formOf)
    const shown = call && words.length > 1 ? [`${words[0]},`, ...words.slice(1)] : words

    return { words, text: `${shown.join(' ')}${call ? '!' : '।'}` }
}

// ---------------------------------------------------------------- words that call for a case (docs/03 §5.3)

export interface CaseWord {
    id: string
    word: FormRef
    meaning: Lang
    takes: Case
    refs: FormRef[]
    /** Index of the word in the case it calls for. */
    focus: number
    hi: string
    en: string
}

export const CASE_WORDS: CaseWord[] = [
    {
        id: 'sah',
        word: AV('सह'),
        meaning: { hi: 'साथ', en: 'with' },
        takes: 'tritiya',
        refs: [N('पुत्त', 'prathama', 'eka'), N('जणअ', 'tritiya', 'eka'), AV('सह'), V('गच्छ', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'पुत्र पिता के साथ जाता है।',
        en: 'The son goes with his father.'
    },
    {
        id: 'vina',
        word: AV('विणा'),
        meaning: { hi: 'बिना', en: 'without' },
        takes: 'tritiya',
        refs: [N('छत्त', 'prathama', 'eka'), N('पोत्थअ', 'tritiya', 'eka'), AV('विणा'), AV('ण'), V('पढ', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'छात्र पुस्तक के बिना नहीं पढ़ता।',
        en: 'The student does not read without a book.'
    },
    {
        id: 'bih',
        word: V('बीह', 'vartaman', 'anya', 'eka'),
        meaning: { hi: 'डरता है (जिससे डर लगे, वह पंचमी में)', en: 'fears (what is feared stands in पंचमी)' },
        takes: 'panchami',
        refs: [N('मिअ', 'prathama', 'eka'), N('सीह', 'panchami', 'eka'), V('बीह', 'vartaman', 'anya', 'eka')],
        focus: 1,
        hi: 'मृग सिंह से डरता है।',
        en: 'The deer fears the lion.'
    }
]

// ---------------------------------------------------------------- rule numbers

const NEUTER_OWN: Case[] = ['prathama', 'dvitiya', 'sambodhan']

/**
 * The book rule for one case of a noun, read from its rules string. The engine lists one group per case in table
 * order (प्रथमा … सम्बोधन); a ninth group belongs to the neuter, whose प्रथमा, द्वितीया and सम्बोधन differ.
 * चतुर्थी and षष्ठी also name नि.४६ (the two share their forms). A rules string with fewer groups is returned whole.
 */
export const caseRule = (rules: string, gender: Gender, c: Case): string => {
    const parts = rules.replace(/^नि\.\s*/, '').split(/,\s*/).filter(Boolean)

    if (parts.length < CASES.length) return rules

    const own = gender === 'napum' && parts.length > CASES.length && NEUTER_OWN.includes(c)
    const part = own ? parts[CASES.length] : parts[CASES.indexOf(c)]
    const twin = c === 'chaturthi' || c === 'shashthi' ? ', ४६' : ''

    return `नि.${part}${twin}`
}
